import { useEffect, useState } from 'react';
import { cva } from 'class-variance-authority';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useAuthStore } from '@/stores/use-auth-store';
import { useWorkspaceStore } from '@/stores/use-workspace-store';
import { apiFetch } from '@/lib/http/client';
import { Flex, Text, Button, message, Spin } from '@nula/core-ui';
import styles from './invitation-accept.module.css';

export const PENDING_INVITATION_KEY = 'pending_invitation_token';
export const PENDING_INVITATION_VARIANT = 'pending_invitation_variant';
// Variants: 'entity' (default — /invitations/{token}/accept) | 'link' (POST /invitations/accept-link)

const root = cva(styles.root);
const card = cva(styles.card);

interface AcceptResponse {
  kind: 'document' | 'collection' | 'recording';
  resourceId: string;
  workspaceId: string;
  permission: string;
}

type ErrorState = 'expired' | 'generic' | null;

export function InvitationAccept() {
  const { token } = useParams({ strict: false }) as { token: string };
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isLoading = useAuthStore((s) => s.isLoading);
  const [errorState, setErrorState] = useState<ErrorState>(null);

  useEffect(() => {
    if (!token) return;
    if (isLoading) return;

    // Logged-out: stash token and redirect to /login.
    // After sign-in, useSessionResume transitions isAuthenticated to true and
    // NulaApp picks up the pending token from sessionStorage to accept it.
    if (!user) {
      sessionStorage.setItem(PENDING_INVITATION_KEY, token);
      void navigate({ to: '/login', replace: true });
      return;
    }

    // Logged-in: accept the invitation
    apiFetch<AcceptResponse>(`/invitations/${token}/accept`, { method: 'POST' })
      .then((data) => {
        message.success('Invitation accepted! You now have access.');
        // T10 route cutover: every entity kind routes through the unified
        // /$workspaceId/$entityId deep-link. The server now ships the
        // workspaceId on the accept response so we can resolve the URL
        // without a separate lookup. Falls back to the personal workspace
        // if it's missing (defensive — server always sends it for entity
        // invitations).
        const workspaceId = data.workspaceId || useWorkspaceStore.getState().activeWorkspaceId;
        if (workspaceId) {
          void navigate({
            to: '/$workspaceId/$entityId',
            params: { workspaceId, entityId: data.resourceId },
            replace: true,
          });
        } else {
          void navigate({ to: '/', replace: true });
        }
      })
      .catch((err: unknown) => {
        const status = (err as { status?: number }).status;
        if (status === 410) {
          setErrorState('expired');
        } else {
          setErrorState('generic');
        }
      });
  }, [token, isLoading, user, navigate]);

  if (errorState === 'expired') {
    return (
      <Flex align="center" justify="center" className={root()}>
        <Flex vertical align="center" gap="md" className={card()}>
          <Text size="lg" strong>Invitation expired</Text>
          <Text type="secondary">This invitation link has expired. Ask for a new invite from the document owner.</Text>
          <Button variant="default" onClick={() => void navigate({ to: '/' })}>
            Go to home
          </Button>
        </Flex>
      </Flex>
    );
  }

  if (errorState === 'generic') {
    return (
      <Flex align="center" justify="center" className={root()}>
        <Flex vertical align="center" gap="md" className={card()}>
          <Text size="lg" strong>Something went wrong</Text>
          <Text type="secondary">We couldn't accept this invitation. Please try again later.</Text>
          <Button variant="default" onClick={() => void navigate({ to: '/' })}>
            Go to home
          </Button>
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex align="center" justify="center" className={root()}>
      <Flex vertical align="center" gap="md">
        <Spin size="lg" />
        <Text type="secondary">Accepting invitation...</Text>
      </Flex>
    </Flex>
  );
}
